// src/pages/admin/AdminWithdrawals.js
// Admin : demandes de paiement des partenaires (virement / especes / cheque)
// Maxula — v1.419.0
//
// Cet ecran est la FILE DE TRAVAIL du guichet : les demandes echues remontent
// en tete, on voit d'un coup d'oeil ce qu'il faut regler aujourd'hui et par
// quel moyen. La validation ouvre une fenetre qui exige la date reelle du
// reglement et sa reference (numero de cheque...), pour que l'historique reste
// exploitable en comptabilite.

import React, { useEffect, useState, useCallback } from 'react';
import { FiCheck, FiX, FiCreditCard, FiDollarSign, FiFileText, FiCalendar, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { walletAPI } from '../../services/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR');

const STATUS = {
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600'
};
const STATUS_LABEL = {
  pending: 'En attente', completed: 'Payé', rejected: 'Refusé', cancelled: 'Annulée'
};

const FILTERS = [
  { key: 'pending', label: 'En attente' },
  { key: 'completed', label: 'Payés' },
  { key: 'rejected', label: 'Refusés' },
  { key: 'cancelled', label: 'Annulés' },
  { key: 'all', label: 'Tous' }
];

const METHODS = [
  { key: '', label: 'Tous les modes', icon: null },
  { key: 'bank_transfer', label: 'Virement / RIB', icon: FiCreditCard },
  { key: 'cash', label: 'Espèces', icon: FiDollarSign },
  { key: 'cheque', label: 'Chèque', icon: FiFileText }
];
const METHOD_ICONS = { bank_transfer: FiCreditCard, cash: FiDollarSign, cheque: FiFileText };

// Date civile sans heure — coherent avec l'espace partenaire.
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(String(d).length <= 10 ? `${d}T12:00:00Z` : d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('fr-FR');
};

// Valeur par defaut du champ date : aujourd'hui au format attendu par
// <input type="date">. toLocaleDateString renverrait jj/mm/aaaa et laisserait
// le champ VIDE, sans aucun message d'erreur.
const todayInput = () => new Date().toISOString().slice(0, 10);

const AdminWithdrawals = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [methodFilter, setMethodFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Fenetre de reglement : date reelle + reference.
  const [settle, setSettle] = useState(null);
  const [paidAt, setPaidAt] = useState(todayInput());
  const [paymentNote, setPaymentNote] = useState('');

  const load = useCallback(async (st, me) => {
    try {
      setLoading(true);
      const res = await walletAPI.adminWithdrawalsFiltered(st, me);
      setItems(res.data?.data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(filter, methodFilter); }, [filter, methodFilter, load]);

  const openSettle = (tx) => {
    setSettle(tx);
    setPaidAt(todayInput());
    // Une note deja saisie par le partenaire est reprise, pour ne pas la perdre.
    setPaymentNote(tx.paymentNote || '');
  };

  const confirmSettle = async () => {
    if (!settle) return;
    const id = settle._id || settle.id;
    // Controle a l'ecran EN PLUS du controle serveur : l'admin voit son erreur
    // immediatement au lieu de la decouvrir via un toast d'echec.
    if (settle.paymentMethod === 'cheque' && !paymentNote.trim()) {
      return toast.error('Indiquez le numéro du chèque.');
    }
    try {
      setBusy(id);
      await walletAPI.process(id, 'approve', { paidAt, paymentNote: paymentNote.trim() });
      toast.success('Règlement enregistré');
      setSettle(null);
      load(filter, methodFilter);
    } catch (e) { toast.error(e?.response?.data?.message || 'Erreur'); }
    finally { setBusy(null); }
  };

  const reject = async (tx) => {
    const id = tx._id || tx.id;
    try {
      setBusy(id);
      await walletAPI.process(id, 'reject');
      toast.success('Demande refusée');
      load(filter, methodFilter);
    } catch (e) { toast.error('Erreur'); }
    finally { setBusy(null); }
  };

  const dueCount = items.filter((t) => t.isDue).length;

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Paiements partenaires</h1>
      <p className="text-gray-500 mb-4">
        Chaque demande est due <strong>20 jours ouvrés</strong> après son dépôt (week-ends et jours fériés exclus).
        Les règlements en espèces et par chèque sont retirés au guichet Maxula.
      </p>

      {/* Compteur d'echeances : seule information vraiment actionnable de la
          page, donc placee avant les filtres. */}
      {filter === 'pending' && dueCount > 0 && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-2">
          <FiAlertCircle className="text-amber-500 shrink-0" size={18} />
          <span className="text-sm text-amber-800">
            <strong>{dueCount}</strong> paiement(s) arrivé(s) à échéance — à régler.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${filter === f.key ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {METHODS.map((m) => {
          const Icon = m.icon;
          return (
            <button key={m.key} onClick={() => setMethodFilter(m.key)}
              className={`px-3 py-1.5 rounded-lg text-sm border inline-flex items-center gap-1.5 ${methodFilter === m.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300'}`}>
              {Icon ? <Icon size={13} /> : null} {m.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((t) => {
            const id = t._id || t.id;
            const MIcon = METHOD_ICONS[t.paymentMethod] || FiCreditCard;
            return (
              <div key={id}
                className={`bg-white rounded-xl border p-4 flex items-start justify-between flex-wrap gap-3 ${t.isDue ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">
                    {t.merchantId?.businessName || '—'} — {fmt(t.amount)} MAD
                  </div>
                  <div className="text-xs text-gray-400">
                    Demande : {fmtDate(t.createdAt)} · {t.reference}
                  </div>

                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-700 font-medium">
                      <MIcon size={13} /> {t.paymentMethodLabel || 'Virement bancaire / RIB'}
                      {t.isPickup && <span className="text-gray-400 font-normal">(guichet)</span>}
                    </span>
                    {t.scheduledPaymentDate && (
                      <span className={`inline-flex items-center gap-1 ${t.isDue ? 'text-amber-700 font-semibold' : 'text-blue-700'}`}>
                        <FiCalendar size={12} /> Prévu le {fmtDate(t.scheduledPaymentDate)}
                        {t.isDue ? ' — échu' : ''}
                      </span>
                    )}
                    {t.paidAt && <span className="text-green-700">Payé le {fmtDate(t.paidAt)}</span>}
                  </div>

                  {/* Le RIB n'a de sens que pour un virement : l'afficher sur
                      une demande en especes embrouille le guichet. */}
                  {t.paymentMethod === 'bank_transfer' && t.merchantId?.bankInfo?.rib && (
                    <div className="text-xs text-gray-500 mt-1">
                      RIB : {t.merchantId.bankInfo.rib}{t.merchantId.bankInfo.bankName ? ` · ${t.merchantId.bankInfo.bankName}` : ''}
                    </div>
                  )}
                  {t.paymentNote && <div className="text-xs text-gray-500 mt-1">Réf. règlement : {t.paymentNote}</div>}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS[t.status] || 'bg-gray-100 text-gray-600'}`}>
                    {t.statusLabel || STATUS_LABEL[t.status] || t.status}
                  </span>
                  {t.status === 'pending' && (
                    <>
                      <button onClick={() => openSettle(t)} disabled={busy === id}
                        className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm flex items-center gap-1 hover:bg-green-700 disabled:opacity-60">
                        <FiCheck size={14} /> Marquer payé
                      </button>
                      <button onClick={() => reject(t)} disabled={busy === id}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm flex items-center gap-1 hover:bg-red-700 disabled:opacity-60">
                        <FiX size={14} /> Refuser
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div className="text-center text-gray-400 py-12">Aucune demande.</div>}
        </div>
      )}

      {/* Fenetre de reglement */}
      {settle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !busy && setSettle(null)} />
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Enregistrer le règlement</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {settle.merchantId?.businessName || '—'} · <strong>{fmt(settle.amount)} MAD</strong> ·{' '}
                {settle.paymentMethodLabel || 'Virement bancaire / RIB'}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date réelle du paiement</label>
                <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400" />
                <p className="text-xs text-gray-400 mt-1">
                  Modifiable pour régulariser un règlement effectué au guichet un jour précédent.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {settle.paymentMethod === 'cheque' ? 'Numéro du chèque' : 'Référence du règlement'}
                  {settle.paymentMethod === 'cheque' && <span className="text-red-500"> *</span>}
                </label>
                <input type="text" value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} maxLength={180}
                  placeholder={settle.paymentMethod === 'cheque' ? 'Ex : 4451233'
                    : settle.paymentMethod === 'cash' ? 'Ex : reçu espèces n° 128'
                    : 'Ex : avis de virement n° ...'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400" />
                {settle.paymentMethod === 'cheque' && (
                  <p className="text-xs text-gray-400 mt-1">
                    Obligatoire : sans numéro, impossible de rapprocher le chèque en comptabilité.
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setSettle(null)} disabled={!!busy}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100">
                Annuler
              </button>
              <button onClick={confirmSettle} disabled={!!busy}
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60 inline-flex items-center gap-2">
                <FiCheck size={15} /> {busy ? 'Enregistrement…' : 'Confirmer le paiement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawals;
